// ---------------------------------------------------------------------------
// Prezio Onboard Widget — ProgressBar
// ---------------------------------------------------------------------------
// Animated % progress bar. Reads progress.percentage from SessionContext.
// Falls back to STEP_ORDER position when the backend returns 0.
// ---------------------------------------------------------------------------

import { useSessionState } from '@/hooks/useSession';
import { t } from '@/i18n';
import { getVisibleSteps, getPredefinedSet } from '@/utils/constants';
import styles from './ProgressBar.module.css';

export function ProgressBar() {
  const { session } = useSessionState();
  if (!session) return null;

  const predefined = getPredefinedSet(session.predefined_steps);
  let pct: number;

  if (session.status === 'completed') {
    pct = 100;
  } else {
    const { percentage, completed_steps, remaining_steps } = session.progress;

    if (percentage > 0) {
      // Backend provides a real percentage — use it directly
      pct = percentage;
    } else if (completed_steps.length > 0 || remaining_steps.length > 0) {
      // Backend populates step lists but not percentage — derive it.
      // Exclude predefined steps from both lists so hidden steps don't
      // inflate the total or appear as completed progress.
      const visibleCompleted = completed_steps.filter((s) => !predefined.has(s));
      const visibleRemaining = remaining_steps.filter((s) => !predefined.has(s));
      const total = visibleCompleted.length + visibleRemaining.length;
      pct = total > 0 ? Math.round((visibleCompleted.length / total) * 100) : 0;
    } else {
      // Fallback: derive from current_step position in visible STEP_ORDER
      const visibleSteps = getVisibleSteps(session.predefined_steps);
      const total = visibleSteps.length;
      const currentIdx = session.current_step ? visibleSteps.indexOf(session.current_step) : 0;
      pct = Math.round((Math.max(0, currentIdx) / total) * 100);
    }
  }

  return (
    <div className={styles['wrapper']}>
      <div
        className={styles['track']}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('progress.label', { percentage: String(pct) })}
      >
        <div className={styles['fill']} style={{ width: `${pct}%` }} />
      </div>
      <div className={styles['label']}>
        <span>{pct}%</span>
      </div>
    </div>
  );
}
